/**
 * "What do I need to do now?" — deterministic selection of the active trip and next event.
 * Inputs are confirmed records only; suggestions never become the next obligation.
 */
import type { EventWithPeople, Trip } from './model';
import { localDateOf } from './time';

export type TripPhase = 'active' | 'upcoming' | 'undated' | 'none';

export interface ActiveTripResult {
  trip: Trip | null;
  phase: TripPhase;
  /** Other trips whose dates also include today — the user should choose. */
  overlapping: Trip[];
  overridden: boolean;
}

const INACTIVE = new Set(['archived', 'canceled', 'completed']);

export function pickActiveTrip(trips: Trip[], todayLocal: string, overrideId?: string | null): ActiveTripResult {
  const live = trips.filter((t) => !INACTIVE.has(t.status) && !t.archived_at);
  const override = overrideId ? live.find((t) => t.id === overrideId) : undefined;
  const dated = live.filter((t) => t.start_date && t.end_date);
  const current = dated
    .filter((t) => t.start_date! <= todayLocal && todayLocal <= t.end_date!)
    .sort((a, b) => a.start_date!.localeCompare(b.start_date!));

  if (override) {
    const phase: TripPhase = current.some((t) => t.id === override.id)
      ? 'active'
      : override.start_date && override.start_date > todayLocal ? 'upcoming' : override.start_date ? 'active' : 'undated';
    return { trip: override, phase, overlapping: current.filter((t) => t.id !== override.id), overridden: true };
  }
  if (current.length > 0) {
    return { trip: current[0]!, phase: 'active', overlapping: current.slice(1), overridden: false };
  }
  const upcoming = dated.filter((t) => t.start_date! > todayLocal).sort((a, b) => a.start_date!.localeCompare(b.start_date!));
  if (upcoming.length > 0) return { trip: upcoming[0]!, phase: 'upcoming', overlapping: [], overridden: false };
  const undated = live.filter((t) => !t.start_date).sort((a, b) => b.created_at.localeCompare(a.created_at));
  if (undated.length > 0) return { trip: undated[0]!, phase: 'undated', overlapping: [], overridden: false };
  return { trip: null, phase: 'none', overlapping: [], overridden: false };
}

export type DayMode = 'my' | 'group';

/** Does this event concern this person? Visibility is not attendance (brief §12). */
export function concernsUser(e: EventWithPeople, userId: string): boolean {
  if (e.participant_ids.length > 0) return e.participant_ids.includes(userId);
  if (e.visibility === 'private') return e.created_by === userId;
  if (e.visibility === 'subgroup') return false; // subgroup events need explicit participants to bind someone
  return true; // trip-wide event with no explicit list applies to everyone on the trip
}

export interface NextEventResult {
  /** An event happening right now (e.g. an overnight train), if any. */
  inProgress: EventWithPeople | null;
  next: EventWithPeople | null;
  /** Confirmed items today whose time is missing or unresolved — they cannot be ranked. */
  needsTime: EventWithPeople[];
  minutesUntilNext: number | null;
}

const IGNORED_STATUS = new Set(['canceled', 'completed', 'suggested']);

export function nextEvent(events: EventWithPeople[], nowUtc: Date, opts: { userId: string; mode: DayMode; tz: string }): NextEventResult {
  const nowMs = nowUtc.getTime();
  const today = localDateOf(nowMs, opts.tz);
  const relevant = events.filter((e) => !IGNORED_STATUS.has(e.status) && (opts.mode === 'group' || concernsUser(e, opts.userId)));

  let inProgress: EventWithPeople | null = null;
  let next: EventWithPeople | null = null;
  const needsTime: EventWithPeople[] = [];

  for (const e of relevant) {
    if (e.category === 'accommodation') continue; // stays span days; check-in/out are separate concerns
    const start = e.start_utc ? new Date(e.start_utc).getTime() : null;
    const end = e.end_utc ? new Date(e.end_utc).getTime() : null;
    if (start === null) {
      const day = e.start_date;
      if (day === today && e.start_precision !== 'datetime') needsTime.push(e);
      else if (day === today && e.start_zone_status !== 'resolved' && e.start_zone_status !== 'user_resolved') needsTime.push(e);
      continue;
    }
    if (start <= nowMs && end !== null && end > nowMs) {
      if (!inProgress || start < new Date(inProgress.start_utc!).getTime()) inProgress = e;
      continue;
    }
    if (start > nowMs && (!next || start < new Date(next.start_utc!).getTime())) next = e;
  }
  return {
    inProgress,
    next,
    needsTime,
    minutesUntilNext: next ? Math.round((new Date(next.start_utc!).getTime() - nowMs) / 60_000) : null,
  };
}

/** Events that fall on a local day (by start instant in the given zone, or by date-only value). */
export function eventsOnDay(events: EventWithPeople[], day: string, tz: string): EventWithPeople[] {
  return events
    .filter((e) => (e.start_utc ? localDateOf(e.start_utc, tz) === day : e.start_date === day)
      || (e.category === 'accommodation' && e.start_date && e.end_date && e.start_date <= day && day < e.end_date))
    .sort(compareEvents);
}

export function compareEvents(a: EventWithPeople | { start_utc: string | null; start_date: string | null; sort_key: number }, b: typeof a): number {
  const ad = a.start_utc ?? (a.start_date ? `${a.start_date}T23:59:59Z` : '9999');
  const bd = b.start_utc ?? (b.start_date ? `${b.start_date}T23:59:59Z` : '9999');
  return ad === bd ? a.sort_key - b.sort_key : ad.localeCompare(bd);
}

/** Primary action for the Now card. Never suggests an action the app cannot perform. */
export function primaryActionFor(e: EventWithPeople | null): { label: string; kind: 'directions' | 'open_documents' | 'confirm_time' | 'view' } | null {
  if (!e) return null;
  if (e.start_precision !== 'datetime' || (e.start_zone_status !== 'resolved' && e.start_zone_status !== 'user_resolved')) {
    return { label: 'Confirm missing time', kind: 'confirm_time' };
  }
  if (e.source === 'booking') return { label: 'Open booking', kind: 'open_documents' };
  if (e.venue || e.address || e.origin_name) return { label: 'Get directions', kind: 'directions' };
  return { label: 'View details', kind: 'view' };
}
